/**
 * The top of every screen: a monospace eyebrow, a display-face title, an
 * optional sentence, and the actions. Consistent enough that the eye learns
 * where to look and stops re-reading the chrome.
 */
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface PageHeaderProps {
  /** Small tracked-out label above the title — the section this screen lives in. */
  eyebrow?: string
  title: ReactNode
  /** One sentence. What this screen is for, or the period it covers. */
  description?: ReactNode
  /** Buttons, filters, a period picker. */
  actions?: ReactNode
  /** A row of figures or badges under the header — freshness, totals, counts. */
  meta?: ReactNode
  className?: string
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  meta,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {eyebrow && <p className="eyebrow mb-1.5">{eyebrow}</p>}
          <h1 className="font-display truncate text-xl font-semibold sm:text-2xl">{title}</h1>
          {description && (
            <p className="text-dim mt-1 max-w-2xl text-sm text-pretty">{description}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {meta && <div className="flex flex-wrap items-center gap-x-6 gap-y-2">{meta}</div>}
    </header>
  )
}
